/**
 * voice-v8 — Audio-Level-Meter (Tag 16).
 *
 * Liest RMS aus einem MediaStreamTrack über `AudioContext` + `AnalyserNode`
 * und liefert einen 0..1-Wert pro Animation-Frame an den Callback.
 * Verwendet `requestAnimationFrame` damit das Drawing der Mic-Pulse-Animation
 * synchron mit dem Browser-Repaint läuft.
 */

interface AudioMeterCallback {
  (level: number): void;
}

export class AudioMeter {
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private rafId = 0;
  private buffer: Float32Array<ArrayBuffer> | null = null;
  private cb: AudioMeterCallback | null = null;

  /** Hängt sich an einen Track. Mehrfach-attach ersetzt den vorigen. */
  attach(track: MediaStreamTrack | null, cb: AudioMeterCallback): void {
    this.detach();
    if (!track) return;

    const stream = new MediaStream([track]);
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    // P1.8 (Audit): iOS startet frische AudioContexte gern "suspended" —
    // ohne resume() bleibt der Pegel für immer 0 und die Anzeige lügt.
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.6;
    const source = ctx.createMediaStreamSource(stream);
    source.connect(analyser);

    this.ctx = ctx;
    this.analyser = analyser;
    this.source = source;
    this.buffer = new Float32Array(new ArrayBuffer(analyser.fftSize * 4));
    this.cb = cb;

    const tick = (): void => {
      if (!this.analyser || !this.buffer || !this.cb) return;
      // P1.8: falls iOS den Context nachträglich suspendiert (Tab-Wechsel,
      // Lock-Screen) — bei jedem Frame sanft wieder wecken.
      if (this.ctx?.state === "suspended")
        void this.ctx.resume().catch(() => undefined);
      this.analyser.getFloatTimeDomainData(this.buffer);
      let sum = 0;
      for (let i = 0; i < this.buffer.length; i++) {
        const v = this.buffer[i] ?? 0;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / this.buffer.length);
      // Empirical normalization — at normal-speech volume RMS ≈ 0.05-0.15.
      const level = Math.min(1, rms * 8);
      this.cb(level);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  detach(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
    if (this.source) {
      try {
        this.source.disconnect();
      } catch {
        /* ignore */
      }
      this.source = null;
    }
    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        /* ignore */
      }
      this.analyser = null;
    }
    if (this.ctx) {
      try {
        this.ctx.close();
      } catch {
        /* ignore */
      }
      this.ctx = null;
    }
    this.buffer = null;
    this.cb = null;
  }
}
